import React, { useState, useRef } from 'react';
import {
  Paper, Group, Stack, Text, Title, Badge, Button,
  Loader, Alert, SimpleGrid, Grid, Select
} from '@mantine/core';
import { TextInput } from '@mantine/core';
import { DataGrid } from '@mui/x-data-grid';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, Cell
} from 'recharts';
import { Bar as CJSBar, Line as CJSLine } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Tooltip as CJSTooltip,
  Legend as CJSLegend,
} from 'chart.js';
import { IconRefresh, IconAlertCircle, IconX } from '@tabler/icons-react';
import useAuthenticatedData from '../../../hooks/useAuthenticatedData';
import config from '../../../config';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, CJSTooltip, CJSLegend);

// ─── constants ─────────────────────────────────────────────────────────────

const CHART_COLORS = [
  '#1971c2', '#2f9e44', '#e67700', '#7048e8',
  '#c92a2a', '#0c8599', '#5c7cfa', '#f03e3e', '#9c36b5', '#1098ad',
];

const PRICING_COLORS = {
  'No SSP':        '#868e96',
  'Expired SSP':   '#fa5252',
  '< -5%':         '#e03131',
  '-2% to -5%':    '#fd7e14',
  'Within +-2%':   '#2f9e44',
  '+2% to +5%':    '#1971c2',
  '> +5%':         '#7048e8',
};

const TOOLTIP_STYLE      = { backgroundColor: 'var(--mantine-color-body)', borderColor: 'var(--mantine-color-default-border)', color: 'var(--mantine-color-text)' };
const TOOLTIP_ITEM_STYLE  = { color: 'var(--mantine-color-text)' };
const TOOLTIP_LABEL_STYLE = { color: 'var(--mantine-color-text)' };

const DARK   = 'var(--mantine-color-dark-7)';
const TEXT   = 'var(--mantine-color-text)';
const BORDER = 'var(--mantine-color-dark-4)';

const GRID_SX = {
  border: 0,
  '--DataGrid-containerBackground': `${DARK} !important`,
  '--DataGrid-rowBackground':       `${DARK} !important`,
  '--DataGrid-footerBackground':    `${DARK} !important`,
  color: TEXT, bgcolor: DARK,
  '&.MuiDataGrid-root .MuiDataGrid-container--top [role=row]': { backgroundColor: `${DARK} !important`, color: `${TEXT} !important` },
  '& .MuiDataGrid-columnHeaderTitle': { color: TEXT, fontWeight: 600 },
  '& .MuiDataGrid-columnSeparator':   { color: `${BORDER} !important` },
  '& .MuiDataGrid-filler':            { backgroundColor: `${DARK} !important` },
  '& .MuiDataGrid-row':               { backgroundColor: `${DARK} !important`, color: `${TEXT} !important` },
  '& .MuiDataGrid-row:hover':         { backgroundColor: 'var(--mantine-color-dark-6) !important' },
  '& .MuiDataGrid-cell':              { borderColor: BORDER, color: TEXT, backgroundColor: `${DARK} !important` },
  '& .MuiDataGrid-footerContainer':   { backgroundColor: `${DARK} !important`, borderColor: BORDER, color: TEXT },
  '& .MuiTablePagination-root':       { color: TEXT },
  '& .MuiTablePagination-selectIcon': { color: TEXT },
  '& .MuiSvgIcon-root':               { color: `${TEXT} !important` },
  '& .MuiDataGrid-overlay':           { backgroundColor: `${DARK} !important`, color: TEXT },
  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus': { outline: 'none !important' },
};

const GRANULARITIES = ['Day', 'Week', 'Month', 'Quarter'];
const toDateStr = (d) => d.toISOString().slice(0, 10);
const DEFAULT_FROM = toDateStr(new Date(Date.now() - 365 * 24 * 60 * 60 * 1000));
const DEFAULT_TO   = toDateStr(new Date());

// ─── formatters ────────────────────────────────────────────────────────────

const fmtDollarsK = (v) => {
  const n = Number(v);
  if (isNaN(n)) return '—';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `$${(n / 1_000).toFixed(0)}K`;
  return `$${Math.round(n)}`;
};
const fmtDollars = (v) => v == null ? '—' : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtDevPct  = (v) => v == null ? '—' : `${v >= 0 ? '+' : ''}${Number(v).toFixed(1)}%`;
const fmtPct     = (n, d) => d > 0 ? `${Math.round((n / d) * 100)}%` : '—';

// ─── sub-components ────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, color }) {
  return (
    <Paper withBorder p="md" radius="md" style={{ minWidth: 120 }}>
      <Stack gap={2} align="center">
        <Title order={2} c={color}>{value ?? '—'}</Title>
        <Text size="sm" c="dimmed" ta="center">{label}</Text>
        {sub && <Text size="xs" c="dimmed" ta="center">{sub}</Text>}
      </Stack>
    </Paper>
  );
}

function HBar({ data, title, onBarClick, activeKey, colorFn }) {
  if (!data?.length) return null;
  const formatted = data.map(d => ({
    count:  d.count,
    rawKey: d.key,
    label:  d.key ? (d.key.length > 26 ? d.key.slice(0, 24) + '…' : d.key) : '(Unknown)',
  }));
  const height = Math.max(100, formatted.length * 28 + 20);
  const hasActive = activeKey != null;
  return (
    <Stack gap="xs">
      <Text size="sm" fw={500}>{title}</Text>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={formatted} layout="vertical" margin={{ top: 0, right: 32, bottom: 0, left: 0 }}>
          <XAxis type="number" tick={{ fontSize: 11, fill: 'var(--mantine-color-dimmed)' }} axisLine={false} tickLine={false} />
          <YAxis type="category" dataKey="label" width={175}
            tick={{ fontSize: 11, fill: 'var(--mantine-color-dimmed)' }} axisLine={false} tickLine={false} />
          <Tooltip contentStyle={TOOLTIP_STYLE} itemStyle={TOOLTIP_ITEM_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} />
          <Bar dataKey="count" radius={[0, 3, 3, 0]}
            onClick={(entry) => onBarClick && onBarClick(entry)}
            style={{ cursor: onBarClick ? 'pointer' : 'default' }}>
            {formatted.map((entry, i) => (
              <Cell key={i}
                fill={colorFn ? (colorFn(entry.rawKey) ?? CHART_COLORS[i % CHART_COLORS.length]) : CHART_COLORS[i % CHART_COLORS.length]}
                opacity={!hasActive || entry.rawKey === activeKey ? 1 : 0.3}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Stack>
  );
}

// ─── column definitions ────────────────────────────────────────────────────

const LINE_COLUMNS = [
  {
    field: 'AddedOnDate', headerName: 'Time', width: 130,
    renderCell: p => {
      if (!p.value) return '—';
      const d = new Date(p.value);
      return `${d.getMonth()+1}/${d.getDate()} ${d.getHours()}:${String(d.getMinutes()).padStart(2,'0')}`;
    }
  },
  { field: 'Salesperson', headerName: 'Rep', width: 60 },
  { field: 'DocNo', headerName: 'Quote #', width: 100 },
  {
    field: 'AccountName', headerName: 'Account', width: 130,
    renderCell: p => p.value
      ? <span title={p.value}>{p.value.length > 15 ? p.value.slice(0, 13) + '…' : p.value}</span>
      : '—'
  },
  {
    field: 'IsKeyCustomer', headerName: '★', width: 40,
    renderCell: p => p.value === 'Y' ? <span title="Key Customer" style={{ color: '#fcc419', fontSize: 16 }}>★</span> : null
  },
  { field: 'Partnumber', headerName: 'PN', width: 115 },
  {
    field: 'Description', headerName: 'Description', width: 155,
    renderCell: p => p.value
      ? <span title={p.value}>{p.value.length > 18 ? p.value.slice(0, 16) + '…' : p.value}</span>
      : '—'
  },
  { field: 'Class',     headerName: 'Cl', width: 40 },
  { field: 'Condition', headerName: 'Cond', width: 58 },
  { field: 'Qty',       headerName: 'Qty', width: 52, type: 'number' },
  {
    field: 'UnitPrice', headerName: 'Unit $', width: 80, type: 'number',
    renderCell: p => fmtDollars(p.value)
  },
  {
    field: 'SSP', headerName: 'SSP', width: 80, type: 'number',
    renderCell: p => fmtDollars(p.value)
  },
  {
    field: 'EffSSP', headerName: 'Eff SSP', width: 80, type: 'number',
    renderCell: p => fmtDollars(p.value)
  },
  {
    field: 'SSPDeviationDollars', headerName: 'Dev $', width: 78, type: 'number',
    renderCell: p => {
      if (p.value == null) return '—';
      const v = Number(p.value);
      return <span style={{ color: v < 0 ? '#fa5252' : v > 0 ? '#40c057' : 'inherit' }}>
        {v >= 0 ? '+' : ''}${Math.round(Math.abs(v)).toLocaleString()}
      </span>;
    }
  },
  {
    field: 'SSPDeviationPct', headerName: 'Dev %', width: 72,
    renderCell: p => {
      if (p.value == null) return '—';
      const v = Number(p.value);
      const color = v < -2 ? '#fa5252' : v > 2 ? '#40c057' : 'inherit';
      return <span style={{ color, fontWeight: 600 }}>{v >= 0 ? '+' : ''}{v.toFixed(1)}%</span>;
    }
  },
  {
    field: 'PriceExpired', headerName: 'Exp', width: 52,
    renderCell: p => (p.value === 1 || p.value === true)
      ? <Badge color="red" variant="filled" size="xs">Exp</Badge>
      : null
  },
  { field: 'DOS', headerName: 'DOS', width: 55, type: 'number' },
  { field: 'DOSBucket', headerName: 'Tier', width: 85 },
  {
    field: 'LineTotal', headerName: 'Line $', width: 85, type: 'number',
    renderCell: p => fmtDollars(p.value)
  },
];

// ─── main component ────────────────────────────────────────────────────────

export default function QuotesAnalytics() {
  const [from,        setFrom]        = useState(DEFAULT_FROM);
  const [to,          setTo]          = useState(DEFAULT_TO);
  const [granularity, setGranularity] = useState('Month');
  const [activeSp,    setActiveSp]    = useState(null);   // active salesperson filter
  const [page,        setPage]        = useState(0);
  const [pageSize,    setPageSize]    = useState(50);
  const [sortModel,   setSortModel]   = useState([{ field: 'AddedOnDate', sort: 'desc' }]);

  // Use refs to always have fresh state inside URL builders
  const stateRef  = useRef({});
  const spListRef = useRef([]);
  stateRef.current = { from, to, granularity, activeSp, page, pageSize, sortModel };

  const buildSummaryUrl = (overrides = {}) => {
    const st = { ...stateRef.current, ...overrides };
    const p = new URLSearchParams();
    if (st.from)     p.set('from',        st.from);
    if (st.to)       p.set('to',          st.to);
    if (st.activeSp) p.set('salesperson', st.activeSp);
    p.set('granularity', st.granularity.toLowerCase());
    return `${config.apiBaseUrl}/api/quotesanalytics/summary?${p}`;
  };

  const buildLinesUrl = (overrides = {}) => {
    const st = { ...stateRef.current, ...overrides };
    const p = new URLSearchParams({ page: st.page, pageSize: st.pageSize });
    if (st.sortModel[0]) {
      p.set('sortField', st.sortModel[0].field);
      p.set('sortDir',   st.sortModel[0].sort);
    }
    if (st.from)     p.set('from',        st.from);
    if (st.to)       p.set('to',          st.to);
    if (st.activeSp) p.set('salesperson', st.activeSp);
    return `${config.apiBaseUrl}/api/quotesanalytics/lines?${p}`;
  };

  const [summaryUrl, setSummaryUrl] = useState(buildSummaryUrl);
  const [linesUrl,   setLinesUrl]   = useState(buildLinesUrl);

  const { data: summary,   isLoading: summaryLoading, refetch: refetchSummary } = useAuthenticatedData(summaryUrl);
  const { data: linesData, isLoading: linesLoading,   error: linesError, refetch: refetchLines } = useAuthenticatedData(linesUrl);

  // ── handlers ──────────────────────────────────────────────────────────────

  const applyFilters = () => {
    setPage(0);
    setActiveSp(null);
    setSummaryUrl(buildSummaryUrl({ page: 0, activeSp: null }));
    setLinesUrl(buildLinesUrl({ page: 0, activeSp: null }));
  };

  const changeGranularity = (g) => {
    setGranularity(g);
    setSummaryUrl(buildSummaryUrl({ granularity: g }));
  };

  const selectSalesperson = (val) => {
    const next = val || null;
    setActiveSp(next);
    setPage(0);
    setSummaryUrl(buildSummaryUrl({ activeSp: next }));
    setLinesUrl(buildLinesUrl({ activeSp: next, page: 0 }));
  };

  const clickSalesperson = (entry) => {
    const sp   = entry.rawKey;
    const next = activeSp === sp ? null : sp;
    setActiveSp(next);
    setPage(0);
    setSummaryUrl(buildSummaryUrl({ activeSp: next }));
    setLinesUrl(buildLinesUrl({ activeSp: next, page: 0 }));
  };

  const clearSalesperson = () => {
    setActiveSp(null);
    setPage(0);
    setSummaryUrl(buildSummaryUrl({ activeSp: null }));
    setLinesUrl(buildLinesUrl({ activeSp: null, page: 0 }));
  };

  // ── derived values ────────────────────────────────────────────────────────

  const s    = summary ?? {};
  const kpis = s.kpis  ?? {};
  const rows  = (linesData?.rows ?? []).map((r, i) => ({ id: i, ...r }));
  const total = linesData?.total ?? 0;

  // Cache the full SP list whenever we have unfiltered summary data
  if (!activeSp && s.bySalesperson?.length > 0) {
    spListRef.current = s.bySalesperson;
  }
  const spOptions = spListRef.current.map(sp => ({ value: sp.key, label: sp.key }));

  const expiredPct = kpis.totalLines > 0 ? Math.round((kpis.expiredCount / kpis.totalLines) * 100) : 0;
  const noSspPct   = kpis.totalLines > 0 ? Math.round((kpis.noSspCount   / kpis.totalLines) * 100) : 0;
  const convPct    = kpis.conversionTotal > 0 ? Math.round((kpis.convertedLines / kpis.conversionTotal) * 100) : 0;

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <Stack gap="md" p="md">

      {/* ── Header ── */}
      <Group justify="space-between">
        <Title order={3}>Quote Analytics</Title>
        <Button size="xs" variant="subtle" leftSection={<IconRefresh size={14} />}
          onClick={() => { refetchSummary(); refetchLines(); }}>
          Refresh
        </Button>
      </Group>

      {/* ── Filters ── */}
      <Paper withBorder p="sm" radius="md">
        <Group gap="sm" wrap="wrap" align="flex-end">
          <TextInput type="date" label="From" size="xs" style={{ width: 150 }}
            value={from} onChange={e => setFrom(e.target.value)} />
          <TextInput type="date" label="To" size="xs" style={{ width: 150 }}
            value={to}   onChange={e => setTo(e.target.value)} />
          <Select
            size="xs"
            style={{ width: 190 }}
            placeholder="All salespersons"
            data={spOptions}
            value={activeSp ?? null}
            onChange={selectSalesperson}
            clearable
          />
          <Button size="xs" onClick={applyFilters}>Apply</Button>
        </Group>
      </Paper>

      {/* ── Volume timeline strip ── */}
      <Paper withBorder p="sm" radius="md">
        <Group justify="space-between" mb={4}>
          <Text size="xs" fw={600} tt="uppercase" c="dimmed">Volume over time</Text>
          <Group gap={4}>
            {GRANULARITIES.map(g => (
              <Button key={g} size="xs" compact="true"
                variant={granularity === g ? 'filled' : 'subtle'}
                onClick={() => changeGranularity(g)}>
                {g}
              </Button>
            ))}
          </Group>
        </Group>
        {s.timeSeries?.length > 0 ? (() => {
          const isDayWeek = ['day','week'].includes(granularity.toLowerCase());
          const cjsData = {
            labels: s.timeSeries.map(d => d.period),
            datasets: [
              {
                label: 'Lines',
                data: s.timeSeries.map(d => d.lineCount),
                backgroundColor: '#4dabf7',
                borderRadius: 3,
                borderSkipped: false,
                yAxisID: 'yLines',
              },
              {
                label: 'Revenue',
                data: s.timeSeries.map(d => Number(d.dollars) || 0),
                backgroundColor: '#2f9e44',
                borderRadius: 3,
                borderSkipped: false,
                yAxisID: 'yRev',
              },
            ],
          };
          const cjsOptions = {
            responsive: true,
            maintainAspectRatio: false,
            animation: false,
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (ctx) => ctx.dataset.label === 'Lines'
                    ? `Lines: ${ctx.parsed.y.toLocaleString()}`
                    : `Revenue: ${fmtDollars(ctx.parsed.y)}`,
                },
                bodyColor: '#c1c2c5',
                backgroundColor: '#1a1b1e',
                borderColor: '#373a40',
                borderWidth: 1,
                titleColor: '#909296',
              },
            },
            scales: {
              x: {
                grid: { display: false },
                border: { display: false },
                ticks: {
                  color: '#909296',
                  font: { size: 10 },
                  maxRotation: isDayWeek ? 45 : 0,
                  minRotation: isDayWeek ? 45 : 0,
                },
              },
              yLines: { display: false, beginAtZero: true },
              yRev:   { display: false, beginAtZero: true },
            },
          };
          return (
            <div style={{ height: isDayWeek ? 155 : 120, position: 'relative' }}>
              <CJSBar data={cjsData} options={cjsOptions} />
            </div>
          );
        })() : (
          summaryLoading
            ? <Group justify="center" style={{ height: 120 }}><Loader size="sm" /></Group>
            : <Group justify="center" style={{ height: 120 }}><Text size="xs" c="dimmed">No data</Text></Group>
        )}
      </Paper>

      {/* ── Pricing quality time series ── */}
      {s.pricingTimeSeries?.length > 0 && (() => {
        const isDayWeek = ['day','week'].includes(granularity.toLowerCase());
        const pqData = {
          labels: s.pricingTimeSeries.map(d => d.period),
          datasets: [
            { label: 'Good / Unexpired', data: s.pricingTimeSeries.map(d => d.goodCount),    backgroundColor: '#2f9e44', borderRadius: 3, borderSkipped: false },
            { label: 'Expired SSP',      data: s.pricingTimeSeries.map(d => d.expiredCount), backgroundColor: '#fa5252', borderRadius: 3, borderSkipped: false },
            { label: 'No SSP',           data: s.pricingTimeSeries.map(d => d.noSspCount),   backgroundColor: '#868e96', borderRadius: 3, borderSkipped: false },
          ],
        };
        const pqOptions = {
          responsive: true, maintainAspectRatio: false, animation: false,
          plugins: {
            legend: { display: true, position: 'top', labels: { color: '#909296', font: { size: 10 }, boxWidth: 12, padding: 8 } },
            tooltip: { bodyColor: '#c1c2c5', backgroundColor: '#1a1b1e', borderColor: '#373a40', borderWidth: 1, titleColor: '#909296' },
          },
          scales: {
            x: { grid: { display: false }, border: { display: false }, ticks: { color: '#909296', font: { size: 10 }, maxRotation: isDayWeek ? 45 : 0, minRotation: isDayWeek ? 45 : 0 } },
            y: { display: false, beginAtZero: true },
          },
        };
        return (
          <Paper withBorder p="sm" radius="md">
            <Text size="xs" fw={600} tt="uppercase" c="dimmed" mb={4}>Pricing quality over time</Text>
            <div style={{ height: isDayWeek ? 175 : 140, position: 'relative' }}>
              <CJSBar data={pqData} options={pqOptions} />
            </div>
          </Paper>
        );
      })()}

      {/* ── SvSP deviation line chart ── */}
      {s.svspTimeSeries?.length > 0 && (() => {
        const isDayWeek = ['day','week'].includes(granularity.toLowerCase());
        const svspData = {
          labels: s.svspTimeSeries.map(d => d.period),
          datasets: [{
            label: 'Avg Dev % from SSP',
            data: s.svspTimeSeries.map(d => d.avgDeviationPct ?? null),
            borderColor: '#4dabf7',
            backgroundColor: 'rgba(77,171,247,0.10)',
            fill: true,
            tension: 0.3,
            pointRadius: 3,
            pointHoverRadius: 5,
            spanGaps: false,
          }],
        };
        const svspOptions = {
          responsive: true, maintainAspectRatio: false, animation: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: { label: (ctx) => `Dev: ${ctx.parsed.y != null ? fmtDevPct(ctx.parsed.y) : '—'}` },
              bodyColor: '#c1c2c5', backgroundColor: '#1a1b1e', borderColor: '#373a40', borderWidth: 1, titleColor: '#909296',
            },
          },
          scales: {
            x: { grid: { display: false }, border: { display: false }, ticks: { color: '#909296', font: { size: 10 }, maxRotation: isDayWeek ? 45 : 0, minRotation: isDayWeek ? 45 : 0 } },
            y: {
              grid: { color: '#2c2e33' },
              border: { display: false },
              ticks: { color: '#909296', font: { size: 10 }, callback: (v) => `${v >= 0 ? '+' : ''}${v.toFixed(0)}%` },
            },
          },
        };
        return (
          <Paper withBorder p="sm" radius="md">
            <Text size="xs" fw={600} tt="uppercase" c="dimmed" mb={4}>SvSP — Avg deviation from SSP over time</Text>
            <div style={{ height: isDayWeek ? 155 : 130, position: 'relative' }}>
              <CJSLine data={svspData} options={svspOptions} />
            </div>
          </Paper>
        );
      })()}

      {/* ── KPI Cards ── */}
      {summaryLoading ? (
        <Group justify="center"><Loader size="sm" /></Group>
      ) : (
        <SimpleGrid cols={{ base: 2, sm: 3, lg: 6 }} spacing="sm">
          <KpiCard label="Lines Quoted"    value={kpis.totalLines?.toLocaleString()} />
          <KpiCard label="$ Quoted"        value={fmtDollarsK(kpis.totalDollars)} />
          <KpiCard label="Converted"       value={`${convPct}%`}
            sub={`${(kpis.convertedLines ?? 0).toLocaleString()} / ${(kpis.conversionTotal ?? 0).toLocaleString()} lines`} />
          <KpiCard label="Expired Pricing" value={`${expiredPct}%`}
            sub={`${(kpis.expiredCount ?? 0).toLocaleString()} lines`}
            color={expiredPct > 10 ? 'red' : undefined} />
          <KpiCard label="No SSP"          value={`${noSspPct}%`}
            sub={`${(kpis.noSspCount ?? 0).toLocaleString()} lines`}
            color={noSspPct > 15 ? 'orange' : undefined} />
          <KpiCard label="Avg Deviation"
            value={kpis.avgDeviationPct != null ? fmtDevPct(kpis.avgDeviationPct) : '—'}
            color={kpis.avgDeviationPct != null
              ? (kpis.avgDeviationPct < -2 ? 'red' : kpis.avgDeviationPct > 2 ? 'green' : undefined)
              : undefined} />
        </SimpleGrid>
      )}

      {/* ── Breakdown charts ── */}
      {(s.bySalesperson?.length > 0 || s.byCustomerCategory?.length > 0) && (
        <Paper withBorder p="md" radius="md">
          <Grid>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <HBar
                data={s.bySalesperson}
                title="By Salesperson (click to filter lines table)"
                activeKey={activeSp}
                onBarClick={clickSalesperson}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <HBar
                data={s.byCustomerCategory}
                title="By Customer Type"
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <HBar
                data={s.pricingQuality}
                title="Pricing Quality (Dev vs SSP)"
                colorFn={(key) => PRICING_COLORS[key]}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 3 }}>
              <HBar data={s.byPartClass}  title="By Part Class" />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 3 }}>
              <HBar data={s.byCondition}  title="By Condition" />
            </Grid.Col>
          </Grid>
        </Paper>
      )}

      {/* ── Key account summary ── */}
      {s.byKeyCustomer?.length > 0 && (
        <Group gap="sm">
          {s.byKeyCustomer.map(d => (
            <Paper key={d.key} withBorder p="sm" radius="md" style={{ minWidth: 170 }}>
              <Stack gap={2} align="center">
                <Group gap={4}>
                  {d.key === 'Y' && <span style={{ color: '#fcc419', fontSize: 18 }}>★</span>}
                  <Text fw={600} size="sm">{d.key === 'Y' ? 'Key Accounts' : 'Standard'}</Text>
                </Group>
                <Text fw={700} size="xl">{d.count?.toLocaleString()}</Text>
                <Text size="xs" c="dimmed">{fmtDollarsK(d.dollars)}</Text>
              </Stack>
            </Paper>
          ))}
        </Group>
      )}

      {/* ── Active filter banner ── */}
      {activeSp && (
        <Paper withBorder p="xs" radius="md" bg="var(--mantine-color-blue-light)">
          <Group gap="xs" align="center">
            <Text size="sm" fw={500}>Filtered by:</Text>
            <Badge variant="filled" size="md" color="blue">Rep: {activeSp}</Badge>
            <Button size="xs" variant="subtle" color="gray" leftSection={<IconX size={12} />}
              onClick={clearSalesperson}>
              Clear
            </Button>
          </Group>
        </Paper>
      )}

      {/* ── Quote lines table ── */}
      <Stack gap="xs">
        <Text size="xs" fw={600} tt="uppercase" c="dimmed">Quote Lines</Text>
        {linesError && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
            Failed to load quote lines.
          </Alert>
        )}
        <DataGrid
          rows={rows}
          columns={LINE_COLUMNS}
          style={{ height: 600 }}
          loading={linesLoading}
          rowCount={total}
          paginationMode="server"
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={({ page: p, pageSize: ps }) => {
            setPage(p);
            setPageSize(ps);
            setLinesUrl(buildLinesUrl({ page: p, pageSize: ps }));
          }}
          pageSizeOptions={[25, 50, 100]}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={m => {
            setSortModel(m);
            setLinesUrl(buildLinesUrl({ sortModel: m }));
          }}
          disableRowSelectionOnClick
          sx={GRID_SX}
        />
      </Stack>

    </Stack>
  );
}
